import { useState, useEffect } from "react";
import { Disclosure } from "@headlessui/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { MegaMenuConfig } from "./types";
import FeaturedCard from "./featured-card";
import { trackMegaMenuLinkClick } from "@/utils/analytics";
import { getCustomComponent } from "./custom";

interface MobileMenuProps {
  menus: MegaMenuConfig[];
}

export default function MobileMenu({ menus }: MobileMenuProps) {
  const router = useRouter();
  const [menuKey, setMenuKey] = useState(0);

  // Close all disclosures on route change by forcing re-render
  useEffect(() => {
    const handleRouteChange = () => {
      setMenuKey((prev) => prev + 1);
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router.events]);

  // Separate pinned and regular menus
  const pinnedMenus = menus.filter((menu) => menu.mobilePinned);
  const regularMenus = menus.filter((menu) => !menu.mobilePinned);

  // Combine: pinned first, then regular
  const orderedMenus = [...pinnedMenus, ...regularMenus];

  return (
    <div className="space-y-1">
      {orderedMenus.map((menu) => (
        <Disclosure
          key={`${menu.id}-${menuKey}`}
          as="div"
          className="border-b border-gray-200"
        >
          {({ open }) => (
            <>
              <Disclosure.Button className="flex w-full items-center justify-between px-4 py-4 text-left text-base font-medium text-gray-900 hover:bg-gray-50">
                <span className="flex items-center gap-2">
                  {menu.label}
                  {menu.badge && (
                    <span className="rounded-full bg-brand-600 px-2 py-0.5 text-xs font-semibold text-white">
                      {menu.badge}
                    </span>
                  )}
                </span>
                <ChevronDownIcon
                  className={`h-5 w-5 text-gray-500 transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </Disclosure.Button>
              {/*
                Keep panel children mounted (and SSR'd when the mobile drawer
                is in the tree) so link text stays crawlable. Images still wait
                for the section to open via FeaturedCard loadMedia.
              */}
              <Disclosure.Panel unmount={false} className="px-4 pb-6 pt-4">
                {/* Check for custom component first */}
                {(() => {
                  const CustomComponent = getCustomComponent(menu.customComponent);
                  if (CustomComponent) {
                    return <CustomComponent menu={menu} />;
                  }

                  const showViewAllUnderFeatured =
                    !!menu.viewAll &&
                    !!menu.featuredCards &&
                    menu.featuredCards.length > 0;

                  return (
                    <>
                      {/* Helper text */}
                      {menu.helperText && (
                        <p className="mb-4 text-sm text-gray-600">{menu.helperText}</p>
                      )}

                      {/* Featured cards - horizontal scroll on mobile */}
                      {menu.featuredCards && menu.featuredCards.length > 0 && (
                        <div className="mb-4 overflow-x-auto w-full snap-x snap-mandatory">
                          <div className="flex gap-4 pb-2 h-[180px]">
                            {menu.featuredCards.map((card, index) => (
                              <div
                                key={index}
                                className="h-full w-40 flex-shrink-0 snap-start"
                                style={{ aspectRatio: "3 / 2" }}
                              >
                                <FeaturedCard
                                  card={card}
                                  menuId={menu.id}
                                  loadMedia={open}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {showViewAllUnderFeatured && menu.viewAll ? (
                        <div className="mb-6">
                          <Link
                            href={menu.viewAll.url}
                            className="text-sm font-medium text-brand-600 hover:text-brand-700"
                            onClick={() =>
                              trackMegaMenuLinkClick({
                                menu: menu.id,
                                linkText: menu.viewAll!.label,
                                linkUrl: menu.viewAll!.url,
                              })
                            }
                          >
                            {menu.viewAll.label} →
                          </Link>
                        </div>
                      ) : null}

                      {/* Link groups */}
                      {menu.linkGroups && menu.linkGroups.length > 0 && (
                        <div className="space-y-6">
                          {menu.linkGroups.map((group, groupIndex) => (
                            <div key={groupIndex}>
                              {group.headingUrl ? (
                                <Link
                                  href={group.headingUrl}
                                  className="mb-3 flex items-center justify-between gap-2 font-semibold text-gray-900 hover:text-brand-700"
                                  onClick={() =>
                                    trackMegaMenuLinkClick({
                                      menu: menu.id,
                                      linkText: group.heading,
                                      linkUrl: group.headingUrl || "",
                                    })
                                  }
                                >
                                  <span>{group.heading}</span>
                                  <ChevronRightIcon
                                    className="h-5 w-5 shrink-0 text-brand-600"
                                    aria-hidden="true"
                                  />
                                </Link>
                              ) : (
                                <p className="mb-3 font-semibold text-gray-900">
                                  {group.heading}
                                </p>
                              )}
                              {group.items && group.items.length > 0 && (
                                <ul className="space-y-4">
                                  {group.items.map((item, itemIndex) => (
                                    <li key={itemIndex}>
                                      <Link
                                        href={item.url}
                                        className="flex items-center gap-2 text-sm text-brand-700 hover:text-gray-900"
                                        {...(item.target && { target: item.target })}
                                        {...(item.rel && { rel: item.rel })}
                                      >
                                        <span>{item.label}</span>
                                        {item.badge && (
                                          <span className="rounded-full bg-brand-600 px-2 py-0.5 text-xs font-semibold text-white">
                                            {item.badge}
                                          </span>
                                        )}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* View all link at bottom */}
                      {menu.viewAll &&
                      (!menu.featuredCards || menu.featuredCards.length === 0) ? (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <Link
                            href={menu.viewAll.url}
                            className="text-sm font-medium text-brand-600 hover:text-brand-700"
                            onClick={() =>
                              trackMegaMenuLinkClick({
                                menu: menu.id,
                                linkText: menu.viewAll!.label,
                                linkUrl: menu.viewAll!.url,
                              })
                            }
                          >
                            {menu.viewAll.label} →
                          </Link>
                        </div>
                      ) : null}
                    </>
                  );
                })()}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      ))}
    </div>
  );
}
